/**
 * Converts an amount into a money formatted string.
 * @param amount - The amount to convert, as a string or number.
 * @param cents - OPTIONAL: Whether or not the amount is already in cents. Default: true.
 * @returns The amount in money formatted string.
 * @example
 * toMoneyString(100);
 * // '$1.00'
 * toMoneyString(100, false);
 * // '$100.00'
 */
export function toMoneyString(amount: string | number, cents = true): string {
  let newAmount = amount;
  if (!cents) {
    newAmount = toCents(amount);
  }
  const moneyAmount: string = (parseFloat(newAmount as string) / 100).toFixed(
    2,
  );
  const moneyFormat = `${window.ThemeVars?.currency?.symbol ?? ""}${moneyAmount}`;
  return moneyFormat;
}

/**
 * Converts an amount into cents (or pennies etc).
 * @param amount - The amount to convert, as a string or number.
 * @returns The amount in cents.
 * @example
 * toCents('$1.00');
 * // 100
 */
export function toCents(amount: string | number): number {
  const amountStr: string = amount.toString().replace(/[^0-9.]/g, "");
  if (amountStr.includes(".")) {
    let floatAmount: number = parseFloat(amountStr);
    floatAmount *= 100;
    return Math.round(floatAmount);
  }
  return parseInt((parseFloat(amountStr) * 100).toString());
}

export function formatMoney(
  amount: string | number,
  withCurrency = true,
  inCents = true,
) {
  const currencyCode = window.Shopify.currency.active || "USD";
  const countryCode = window.Shopify.country || "US";
  const languageCode = window.Shopify.locale || "en";
  const locale = `${languageCode}-${countryCode}`;
  const parsedAmount =
    typeof amount === "string" ? parseInt(amount) / 100 : amount / 100;

  // Apply toFixed to avoid floating-point precision issues before formatting
  const roundedAmount = parsedAmount.toFixed(inCents ? 2 : 0);

  const formatter = new Intl.NumberFormat(locale, {
    style: withCurrency ? "currency" : "decimal",
    currency: currencyCode,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: inCents ? 2 : 0,
    maximumFractionDigits: inCents ? 2 : 0,
  });

  return formatter.format(parseFloat(roundedAmount));
}
